import React, { useState, useEffect } from 'react';
import { apiGet, apiPut, apiPost, apiDelete, apiFetch, apiThrow } from '../utils/api';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { friendlyErrorMessage } from '../components/ErrorMessage';
import { BTN, BTN_XS } from '../constants/buttons';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { FIELD_LABEL_CLASS } from '../constants/inputs';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';
import { LAST_MANAGER_REASON } from '../constants/lastManager';

const LS_KEYS = {
  requestedBy: 'sfx_requested_by',
  department: 'sfx_department',
  userPhone: 'sfx_user_phone',
  subDepartment: 'sfx_sub_department',
};

// Prefixes a save error with WHICH part of the form it belongs to, so a
// partial-failure toast is actionable rather than a bare "something failed".
// Runs the error through `friendlyErrorMessage` FIRST (a network throw's
// `.message` is the raw "Failed to fetch", and that string's own friendly
// mapping is keyed on an exact/substring match against the UNPREFIXED
// message — prefixing before translating would silently defeat it) and
// returns a plain STRING, so `useToast().error()` — which itself runs
// `friendlyErrorMessage` on whatever it is given — has nothing left to
// reinterpret.
function namedError(section, err) {
  return `${section}: ${friendlyErrorMessage(err)}`;
}

function MySettings() {
  const [form, setForm] = useState({
    requestedBy: '',
    department: '',
    userPhone: '',
    subDepartment: '',
  });
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [subDepartments, setSubDepartments] = useState([]);
  // Get department assignments from context
  const { assignments, activeDepartment, isAdmin } = useDepartment();
  const { user: authUser, mustChangePassword, setMustChangePassword, refreshAuth } = useAuth();
  const toast = useToast();
  const { guard: guardSaveSettings, busy: savingSettings } = useSubmitGuard();

  // Leave department state
  const [leaveModal, setLeaveModal] = useState(null); // { assignment, impact: null | object }
  const [leaveConfirmText, setLeaveConfirmText] = useState('');
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [leaveStep, setLeaveStep] = useState(1); // 1 = normal/warning, 2 = last-buyer final confirm

  // Change password state
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwSubmitting, setPwSubmitting] = useState(false);

  // Change email state
  const [emailForm, setEmailForm] = useState({ newEmail: '', password: '' });
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  // Order-alert mute preferences (per department). Owner ruling 2026-08-05:
  // "we give the supervisor the ability to mute buying notifications etc if
  // another buyer is on board." Per-(user, department) — never global, per
  // the sealed-box model. Default (no fetch error, no preference row) reads
  // as unmuted, matching the server's default.
  const [orderAlertPrefs, setOrderAlertPrefs] = useState([]);
  const [orderAlertLoaded, setOrderAlertLoaded] = useState(false);
  const [orderAlertSavingId, setOrderAlertSavingId] = useState(null);
  const [orderAlertError, setOrderAlertError] = useState('');

  // Load current values from localStorage on mount
  useEffect(() => {
    setForm({
      requestedBy: localStorage.getItem(LS_KEYS.requestedBy) || '',
      department: localStorage.getItem(LS_KEYS.department) || '',
      userPhone: localStorage.getItem(LS_KEYS.userPhone) || '',
      subDepartment: localStorage.getItem(LS_KEYS.subDepartment) || '',
    });
  }, []);

  // Fetch the sub-departments of the ACTIVE department only.
  //
  // The picker used to list every sub-department across all of the user's
  // departments. The save is now scoped to one department and the server
  // validates the name against that department, so an option belonging to
  // another one would simply be rejected — offering it is a broken choice.
  useEffect(() => {
    const deptId = activeDepartment?.department_id;
    if (!deptId) {
      setSubDepartments([]);
      return;
    }
    apiGet(`/api/sub-departments?department_id=${deptId}`)
      .then(r => r.json())
      .then(data => setSubDepartments(Array.isArray(data) ? data : []))
      .catch(() => setSubDepartments([]));
  }, [activeDepartment?.department_id]);

  // Fetch the caller's order-alert mute preferences once. The server already
  // scopes `departments` to the ones the caller holds canManageDepartmentOrders
  // in — a department where the alert would never reach must not offer a
  // switch, and the endpoint enforces that rather than this page re-deriving
  // it (an admin, who cannot mute, simply gets an empty list back).
  useEffect(() => {
    let cancelled = false;
    apiGet('/api/notifications/mute-preferences')
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setOrderAlertPrefs(Array.isArray(data.departments) ? data.departments : []);
      })
      .catch(() => {
        // Silent — the section simply shows nothing rather than an error for
        // a preference nobody has necessarily touched yet.
      })
      .finally(() => {
        if (!cancelled) setOrderAlertLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  // department_name comes from the assignments list (GET /api/roles/my-assignments),
  // not from the mute-preferences endpoint, which reports ids only.
  const departmentNameFor = (departmentId) => {
    const match = (assignments || []).find((a) => a.department_id === departmentId);
    return match?.department_name || `Department #${departmentId}`;
  };

  const handleOrderAlertToggle = async (departmentId, nextMuted) => {
    setOrderAlertError('');
    setOrderAlertSavingId(departmentId);
    // Optimistic — reverted in the catch block on failure.
    setOrderAlertPrefs((prev) => prev.map((d) => (d.department_id === departmentId ? { ...d, muted: nextMuted } : d)));
    try {
      const res = await apiPut(`/api/notifications/mute-preferences/${departmentId}`, { muted: nextMuted });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update order alerts');
    } catch (err) {
      setOrderAlertPrefs((prev) => prev.map((d) => (d.department_id === departmentId ? { ...d, muted: !nextMuted } : d)));
      setOrderAlertError(err.message || 'Failed to update order alerts');
    } finally {
      setOrderAlertSavingId(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (saved) setSaved(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!form.requestedBy.trim()) {
      newErrors.requestedBy = 'Name is required';
    }
    return newErrors;
  };

  const handleSave = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    guardSaveSettings(async () => {
      // These two are never sent to the server at all — nothing to earn, so
      // they are safe to remember immediately.
      localStorage.setItem(LS_KEYS.requestedBy, form.requestedBy.trim());
      localStorage.setItem(LS_KEYS.department, form.department.trim());

      const trimmedPhone = form.userPhone.trim();
      const trimmedSubDept = form.subDepartment || '';

      // Persist PHONE to the database (users table). 🚨 NEVER EMAIL (2026-09-23):
      // email is a credential — `forgot-password` mails the reset token to it —
      // and `PUT /api/users/me` now refuses a changed address
      // (`email_change_requires_verification`). The Change Email form below
      // (password + a link to the new address) is the only way to change it,
      // which is why the profile section shows the address read-only. Pinned
      // on the request body by MySettings.ownEmailReadOnly.test.js.
      // The tick and the localStorage cache are EARNED: `apiPut` RESOLVES on a
      // 4xx/5xx, so the ok check is what a bare `.catch()` cannot see.
      let profileSaved = false;
      try {
        const res = await apiPut('/api/users/me', { phone: trimmedPhone });
        if (!res.ok) await apiThrow(res);
        localStorage.setItem(LS_KEYS.userPhone, trimmedPhone);
        profileSaved = true;
      } catch (err) {
        // Name WHICH part failed — a bare server message here reads as "did
        // the whole save fail?" when only the phone did.
        toast.error(namedError('Phone', err));
      }

      // Also update sub-department in the database, for this user's orders IN THE
      // ACTIVE DEPARTMENT ONLY.
      //
      // This used to send `username: form.requestedBy` — a free-text field — and
      // no department, and the server resolved the target by DISPLAY NAME and
      // rewrote that person's orders in every department. Identity now comes from
      // the session (no id is sent at all) and `department_id` scopes the write.
      // Admins have no active department; there is nothing department-scoped for
      // them to set, so the call is skipped rather than sent unscoped.
      let subDeptSaved = true; // nothing attempted, so nothing to have failed
      if (activeDepartment?.department_id) {
        subDeptSaved = false;
        try {
          const res = await apiPut('/api/sub-departments/user-update', {
            department_id: activeDepartment.department_id,
            sub_department: trimmedSubDept,
          });
          if (!res.ok) await apiThrow(res);
          localStorage.setItem(LS_KEYS.subDepartment, trimmedSubDept);
          subDeptSaved = true;
        } catch (err) {
          // Name WHICH part failed — this is an order-routing field, a
          // different consequence from the email/phone credential above, and
          // the user needs to know which one to go fix.
          toast.error(namedError('Sub-department', err));
        }
      } else {
        // No active department — nothing department-scoped to set, so this
        // field is a plain display preference here and is safe to remember.
        localStorage.setItem(LS_KEYS.subDepartment, trimmedSubDept);
      }

      // The tick is EARNED: only when every write actually attempted was
      // accepted by the server. Partial success must never read as total
      // success — the earlier fields already have their own tick-equivalent
      // (their localStorage write happened above, on their own success).
      if (profileSaved && subDeptSaved) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    });
  };

  const handleLeaveClick = async (assignment) => {
    setLeaveModal({ assignment, impact: null });
    setLeaveConfirmText('');
    setLeaveError('');
    setLeaveStep(1);
    try {
      const res = await apiGet(`/api/departments/${assignment.department_id}/leave-check`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to check impact');
      setLeaveModal(prev => prev ? { ...prev, impact: data } : null);
    } catch (err) {
      setLeaveModal(prev => prev ? { ...prev, impact: { error: err.message } } : null);
    }
  };

  // Escape key closes leave modal
  useEffect(() => {
    if (!leaveModal) return;
    const handler = (e) => {
      if (e.key === 'Escape' && !leaveLoading) {
        setLeaveModal(null);
        setLeaveConfirmText('');
        setLeaveError('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [leaveModal, leaveLoading]);

  const handleLeaveConfirm = async () => {
    if (!leaveModal) return;

    // If last buyer and still on step 1, advance to step 2 (final confirmation)
    if (leaveModal.impact?.is_last_buyer && leaveStep === 1) {
      setLeaveStep(2);
      return;
    }

    setLeaveLoading(true);
    setLeaveError('');
    try {
      const res = await apiDelete(`/api/departments/${leaveModal.assignment.department_id}/leave`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to leave department');
      setLeaveModal(null);
      setLeaveConfirmText('');
      setLeaveStep(1);
      window.dispatchEvent(new Event('sfx-role-change'));
    } catch (err) {
      setLeaveError(err.message);
    } finally {
      setLeaveLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
      errors[field] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
    }`;

  // 🎨 Owner ruling 2026-09-28: every field caption matches the order form's
  // item-label style — see `constants/inputs.js#FIELD_LABEL_CLASS`.
  const labelClass = FIELD_LABEL_CLASS;

  return (
    <div className="max-w-xl mx-auto p-2.5" data-mobile-inputs="">
      {mustChangePassword && (
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg text-amber-800 dark:text-amber-200 text-sm font-medium" role="alert" data-testid="force-password-change-banner">
          You must change your default password before continuing. Please update your password below.
        </div>
      )}
      <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-1 lg:hidden">My Settings</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Update your profile details. These will auto-populate when you create new orders.
      </p>

      {saved && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg text-green-700 dark:text-green-300 text-sm" role="alert">
          ✓ Settings saved successfully
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-5">
        {/* Name */}
        <div>
          <label htmlFor="requestedBy" className={labelClass}>Name *</label>
          <input
            type="text"
            id="requestedBy"
            name="requestedBy"
            value={form.requestedBy}
            onChange={handleChange}
            placeholder="Your full name"
            className={inputClass('requestedBy')}
            aria-invalid={!!errors.requestedBy}
            aria-describedby={errors.requestedBy ? 'requestedBy-error' : undefined}
          />
          {errors.requestedBy && (
            <p id="requestedBy-error" role="alert" className="text-red-500 text-xs mt-1">{errors.requestedBy}</p>
          )}
        </div>

        {/* Department Assignments (Read-Only) */}
        <div data-testid="department-assignments-section">
          <label className={labelClass}>Department Assignment(s)</label>
          {isAdmin ? (
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded-full text-xs font-medium">
                Global Admin
              </span>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                As an admin, you have access to all departments and productions.
              </p>
            </div>
          ) : assignments && assignments.length > 0 ? (
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm space-y-2">
              {assignments.map((a, idx) => (
                <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`dept-assignment-${idx}`}>
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {a.department_name || `Department #${a.department_id}`}
                  </span>
                  {a.production_name && (
                    <span className="text-gray-500 dark:text-gray-400">
                      — {a.production_name}
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    a.role === 'buyer' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                    a.role === 'sub_department_lead' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {a.role === 'sub_department_lead' ? 'Sub-Dept Lead' : a.role === 'buyer' ? 'Buyer' : a.role || 'User'}
                  </span>
                  {a.sub_department_name && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({a.sub_department_name})
                    </span>
                  )}
                  <button
                    onClick={() => handleLeaveClick(a)}
                    className={BTN_XS['danger-soft']}
                    title="Leave this department"
                  >
                    Leave
                  </button>
                </div>
              ))}
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Department assignments are managed by your Admin or Buyer. You can leave a department at any time.
              </p>
            </div>
          ) : (
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400">
              No department assignments yet. Contact your Admin or Buyer to be assigned to a department.
            </div>
          )}
        </div>

        {/* Sub-Department (read-only display from assignment) */}
        {activeDepartment && activeDepartment.sub_department_name && (
          <div>
            <label className={labelClass}>Sub-Department</label>
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-800 dark:text-gray-200" data-testid="sub-department-display">
              {activeDepartment.sub_department_name}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Sub-department is set by your department assignment</p>
          </div>
        )}

        {/* Email (read-only — same display pattern as Sub-Department above).
            Changing it is the Change Email form's job: it needs the current
            password and a link sent to the new address. */}
        <div>
          <label className={labelClass}>Email Address</label>
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-800 dark:text-gray-200" data-testid="account-email-display">
            {authUser?.email || 'Not set'}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">To change your email, use Change Email below</p>
        </div>

        {/* Phone Number */}
        <div>
          <label htmlFor="userPhone" className={labelClass}>Phone Number</label>
          <input
            type="tel"
            id="userPhone"
            name="userPhone"
            value={form.userPhone}
            onChange={handleChange}
            placeholder="+44 7700 000000"
            className={inputClass('userPhone')}
          />
        </div>

        {/* Save Button */}
        <div className="pt-2">
          <button type="submit" disabled={savingSettings} className={BTN.confirm}>
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* ── Change Password ── */}
      <div className="mt-10 pt-8 border-t border-gray-200 dark:border-gray-700">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Change Password</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Update your password. You'll stay logged in on this device.</p>

        {pwSuccess && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg text-green-700 dark:text-green-300 text-sm">
            {pwSuccess}
          </div>
        )}
        {pwError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {pwError}
          </div>
        )}

        <form onSubmit={async (e) => {
          e.preventDefault();
          setPwError(''); setPwSuccess('');
          if (pwForm.newPassword.length < 8) { setPwError('New password must be at least 8 characters'); return; }
          if (pwForm.newPassword !== pwForm.confirmPassword) { setPwError('New passwords do not match'); return; }
          setPwSubmitting(true);
          try {
            const res = await apiPost('/api/auth/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to change password');
            setPwSuccess(data.message || 'Password changed successfully.');
            setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            // Clear forced password change flag if it was set
            if (mustChangePassword) {
              setMustChangePassword(false);
              refreshAuth();
            }
            setTimeout(() => setPwSuccess(''), 5000);
          } catch (err) { setPwError(err.message); }
          finally { setPwSubmitting(false); }
        }} className="space-y-4">
          <div>
            <label htmlFor="currentPassword" className={labelClass}>Current Password</label>
            <input type="password" id="currentPassword" autoComplete="current-password"
              value={pwForm.currentPassword} onChange={(e) => setPwForm(p => ({ ...p, currentPassword: e.target.value }))}
              className={inputClass('currentPassword')} required />
          </div>
          <div>
            <label htmlFor="newPassword" className={labelClass}>New Password</label>
            <input type="password" id="newPassword" autoComplete="new-password"
              value={pwForm.newPassword} onChange={(e) => setPwForm(p => ({ ...p, newPassword: e.target.value }))}
              className={inputClass('newPassword')} required placeholder="At least 8 characters" />
            {pwForm.newPassword.length > 0 && pwForm.newPassword.length < 8 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{8 - pwForm.newPassword.length} more character{8 - pwForm.newPassword.length !== 1 ? 's' : ''} needed</p>
            )}
          </div>
          <div>
            <label htmlFor="confirmNewPassword" className={labelClass}>Confirm New Password</label>
            <input type="password" id="confirmNewPassword" autoComplete="new-password"
              value={pwForm.confirmPassword} onChange={(e) => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))}
              className={inputClass('confirmNewPassword')} required />
            {pwForm.confirmPassword.length > 0 && pwForm.newPassword !== pwForm.confirmPassword && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">Passwords do not match</p>
            )}
          </div>
          <button type="submit" disabled={pwSubmitting} className={BTN.confirm}>
            {pwSubmitting ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* ── Change Email ── */}
      <div className="mt-10 pt-8 border-t border-gray-200 dark:border-gray-700">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Change Email</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Current email: <strong className="text-gray-700 dark:text-gray-300">{authUser?.email || 'Not set'}</strong>
        </p>

        {emailSuccess && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg text-green-700 dark:text-green-300 text-sm">
            {emailSuccess}
          </div>
        )}
        {emailError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {emailError}
          </div>
        )}

        <form onSubmit={async (e) => {
          e.preventDefault();
          setEmailError(''); setEmailSuccess('');
          setEmailSubmitting(true);
          try {
            const res = await apiPost('/api/auth/change-email', { newEmail: emailForm.newEmail, password: emailForm.password });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to request email change');
            setEmailSuccess(data.message || 'Verification email sent to your new address.');
            setEmailForm({ newEmail: '', password: '' });
          } catch (err) { setEmailError(err.message); }
          finally { setEmailSubmitting(false); }
        }} className="space-y-4">
          <div>
            <label htmlFor="newEmail" className={labelClass}>New Email Address</label>
            <input type="email" id="newEmail" autoComplete="email"
              value={emailForm.newEmail} onChange={(e) => setEmailForm(p => ({ ...p, newEmail: e.target.value }))}
              className={inputClass('newEmail')} required placeholder="new@example.com" />
          </div>
          <div>
            <label htmlFor="emailPassword" className={labelClass}>Current Password</label>
            <input type="password" id="emailPassword" autoComplete="current-password"
              value={emailForm.password} onChange={(e) => setEmailForm(p => ({ ...p, password: e.target.value }))}
              className={inputClass('emailPassword')} required placeholder="Confirm your identity" />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Required to confirm the email change</p>
          </div>
          <button type="submit" disabled={emailSubmitting} className={BTN.confirm}>
            {emailSubmitting ? 'Sending verification...' : 'Change Email'}
          </button>
        </form>
      </div>

      {/* Order Alerts (per-department mute) */}
      {orderAlertLoaded && orderAlertPrefs.length > 0 && (
        <div className="mt-10 pt-8 border-t border-gray-200 dark:border-gray-700" data-testid="order-alert-mute-section">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Order Alerts</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Choose which of your departments notify you when a new order needs buying. Turn one off once another buyer has it covered — you'll still get everything else: approvals, rejections, deliveries and hire alerts.
          </p>
          {orderAlertError && (
            <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm" role="alert">
              {orderAlertError}
            </div>
          )}
          <div className="space-y-2">
            {orderAlertPrefs.map((pref) => {
              const enabled = !pref.muted;
              const saving = orderAlertSavingId === pref.department_id;
              return (
                <div
                  key={pref.department_id}
                  className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                  data-testid={`order-alert-toggle-${pref.department_id}`}
                >
                  <span className="text-gray-800 dark:text-gray-200">{departmentNameFor(pref.department_id)}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`Order alerts for ${departmentNameFor(pref.department_id)}`}
                    onClick={() => handleOrderAlertToggle(pref.department_id, !pref.muted)}
                    disabled={saving}
                    title={enabled ? 'Order alerts on — click to mute' : 'Order alerts muted — click to turn back on'}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                      enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Download My Data */}
      <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Your Data</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Download a copy of all your personal data including your profile, orders, and activity history.
        </p>
        <ExportDataButton />
      </div>

      {/* Request Account Deletion (GDPR Article 17) */}
      <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Delete Your Account</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
          To request deletion of your personal data under GDPR Article 17 (right to erasure), email
          our support team. We&rsquo;ll action your request within 30 days.
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Deletion removes your email, phone, personal reference, and name from our systems. Order
          records are retained under the ICO legitimate-business-records exemption for accounting
          and audit purposes — these retained records do not identify you by current contact details.
        </p>
        <a
          href="mailto:support@crewforge.co.uk?subject=Account%20deletion%20request"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg border border-red-200 dark:border-red-800 shadow-sm"
        >
          Email support@crewforge.co.uk
        </a>
      </div>

      {/* Leave Department Confirmation Modal */}
      {leaveModal && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 ${MODAL_OVERLAY}`} role="dialog" aria-modal="true" aria-labelledby="leave-modal-title" onClick={() => { if (!leaveLoading) { setLeaveModal(null); setLeaveConfirmText(''); setLeaveError(''); } }}>
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 ${MODAL_PANEL_MOBILE_CAP}`} onClick={(e) => e.stopPropagation()}>
            <h3 id="leave-modal-title" className="text-lg font-bold text-gray-900 dark:text-white mb-3">Leave Department?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              You are about to leave <strong>{leaveModal.assignment.department_name || `Department #${leaveModal.assignment.department_id}`}</strong>
              {leaveModal.assignment.production_name && <> on <strong>{leaveModal.assignment.production_name}</strong></>}.
            </p>

            {/* Impact warnings */}
            {leaveModal.impact === null && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Checking impact...</p>
            )}
            {leaveModal.impact?.error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {leaveModal.impact.error}
              </div>
            )}
            {/* Last-manager floor (owner, 2026-10-08): the server refuses this leave
                (409 last_department_manager), so explain instead of offering the confirm. */}
            {leaveModal.impact?.last_manager_locked && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg" data-testid="leave-last-manager">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">You can't leave this department yet.</p>
                {/* Ruling D: the only member is told the way out (the server's sentence). */}
                <p className="text-sm text-amber-700 dark:text-amber-400">{leaveModal.impact.leave_advice_message || LAST_MANAGER_REASON}</p>
              </div>
            )}
            {leaveModal.impact && !leaveModal.impact.error && !leaveModal.impact.last_manager_locked && (
              <>
                {/* Last buyer amber warning (step 1) */}
                {leaveModal.impact.is_last_buyer && leaveStep === 1 && (
                  <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">You are the only person who can manage this department's orders.</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Please assign a new order manager before leaving, otherwise the department will have no one to process orders.
                    </p>
                  </div>
                )}

                {/* Last buyer red confirmation (step 2) */}
                {leaveModal.impact.is_last_buyer && leaveStep === 2 && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-300">
                      By leaving, management of this department reverts to the Crew Forge administrator. The department may be archived if nobody new is assigned to manage it. The administrator accepts no responsibility for the ongoing operation of this department.
                    </p>
                  </div>
                )}

                {/* Standard impact warnings */}
                {(leaveModal.impact.workflow_steps > 0 || leaveModal.impact.pending_approvals > 0 || leaveModal.impact.team_memberships > 0 || leaveModal.impact.assigned_tasks > 0) ? (
                  <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">This will affect:</p>
                    <ul className="text-sm text-amber-700 dark:text-amber-400 list-disc list-inside space-y-0.5">
                      {leaveModal.impact.workflow_steps > 0 && (
                        <li>{leaveModal.impact.workflow_steps} approval workflow step(s) will be reassigned to the department buyer</li>
                      )}
                      {leaveModal.impact.pending_approvals > 0 && (
                        <li>{leaveModal.impact.pending_approvals} pending approval(s) will be reassigned</li>
                      )}
                      {leaveModal.impact.team_memberships > 0 && (
                        <li>{leaveModal.impact.team_memberships} team membership(s) will be removed</li>
                      )}
                      {leaveModal.impact.assigned_tasks > 0 && (
                        <li>{leaveModal.impact.assigned_tasks} task assignment(s) will be removed</li>
                      )}
                    </ul>
                  </div>
                ) : !leaveModal.impact.is_last_buyer && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">No pending work will be affected.</p>
                )}
              </>
            )}

            {/* Type-to-confirm input */}
            {leaveStep === 1 && !leaveModal.impact?.last_manager_locked && (
              <div className="mb-4">
                <label className={FIELD_LABEL_CLASS}>
                  Type <strong>LEAVE</strong> to confirm
                </label>
                <input
                  type="text"
                  value={leaveConfirmText}
                  onChange={(e) => setLeaveConfirmText(e.target.value)}
                  placeholder="LEAVE"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  autoFocus
                />
              </div>
            )}

            {/* Error message */}
            {leaveError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {leaveError}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setLeaveModal(null); setLeaveConfirmText(''); setLeaveError(''); setLeaveStep(1); }}
                disabled={leaveLoading}
                className={BTN.secondary}
              >
                {leaveModal.impact?.last_manager_locked ? 'Close' : 'Cancel'}
              </button>
              {leaveModal.impact?.last_manager_locked ? null : leaveStep === 2 ? (
                <button
                  onClick={handleLeaveConfirm}
                  disabled={leaveLoading}
                  className={BTN.danger}
                >
                  {leaveLoading ? 'Leaving...' : 'I understand, leave anyway'}
                </button>
              ) : (
                <button
                  onClick={handleLeaveConfirm}
                  disabled={leaveLoading || leaveConfirmText.trim().toLowerCase() !== 'leave'}
                  className={BTN.danger}
                >
                  {leaveLoading ? 'Leaving...' : 'Leave Department'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExportDataButton() {
  const [downloading, setDownloading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async () => {
    setDownloading(true);
    setDone(false);
    setError('');
    try {
      const response = await apiFetch('/api/users/me/export');
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crewforge-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch (err) {
      console.error('Export failed:', err);
      setError(err.message || 'Export failed');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <button onClick={handleExport} disabled={downloading} className={BTN.secondary}>
        {downloading ? 'Preparing...' : 'Download My Data'}
      </button>
      {done && <span className="text-sm text-green-600 dark:text-green-400">Download started</span>}
      {error && <span className="text-sm text-red-600 dark:text-red-400">{error}</span>}
    </div>
  );
}

export default MySettings;
